import type {ItemFormErrorField, ItemFormValues} from '../../hooks/useItemForm';

export type ItemFormSectionId = 'photos' | 'basics' | 'traits' | 'wearContext' | 'condition' | 'lifecycle';

/** Порядок секций в форме — от «нужно всем» к «нужно редко» (QA-13). */
export const SECTION_ORDER: ItemFormSectionId[] = [
    'photos', 'basics', 'traits', 'wearContext', 'condition', 'lifecycle',
];

export const SECTION_TITLES: Record<ItemFormSectionId, string> = {
    photos: 'Фотографии',
    basics: 'Название и категория',
    traits: 'Характеристики',
    wearContext: 'Когда носить',
    condition: 'Состояние и дефекты',
    lifecycle: 'Покупка и утилизация',
};

/**
 * Поля, по которым решаем, «заполнена» ли секция. `purpose` не учитываем: у него
 * всегда есть значение по умолчанию, иначе секция считалась бы заполненной
 * всегда.
 */
const SECTION_VALUE_FIELDS: Record<ItemFormSectionId, (keyof ItemFormValues)[]> = {
    photos: [],
    basics: ['name', 'categoryId', 'description'],
    traits: ['sizeId', 'conditionId', 'brandId', 'priceRub', 'colorIds', 'materialIds', 'printTypeId', 'fitId'],
    wearContext: ['seasonIds', 'weatherSuitabilityIds', 'occasionIds', 'styleIds', 'layerIds', 'warmthLevelId', 'formalityId'],
    condition: ['defectIds', 'waterProof'],
    lifecycle: ['purchaseDate', 'disposalDate', 'thrownAway'],
};

/** Секция, в которой живёт поле с ошибкой: её раскрываем при неудачном submit. */
export const SECTION_BY_ERROR_FIELD: Record<ItemFormErrorField, ItemFormSectionId> = {
    name: 'basics',
    priceRub: 'traits',
};

function isFilled(value: ItemFormValues[keyof ItemFormValues]): boolean {
    if (Array.isArray(value)) {
        return value.length > 0;
    }
    if (typeof value === 'boolean') {
        return value;
    }
    return String(value ?? '').trim() !== '';
}

/** Есть ли в секции хотя бы одно заполненное поле. */
export function isSectionFilled(id: ItemFormSectionId, values: ItemFormValues): boolean {
    return SECTION_VALUE_FIELDS[id].some(field => isFilled(values[field]));
}

export type ItemFormMode = 'create' | 'edit' | 'aiConfirm';

export type SectionOpenState = Record<ItemFormSectionId, boolean>;

/**
 * Какие секции раскрыты при открытии формы.
 *
 * — create: только фото и главное, остальное свёрнуто — «Добавление за минуту»;
 * — edit и проверка результата AI: раскрыты секции, где уже есть данные, чтобы
 *   заполненное (в том числе подставленное распознаванием) было видно сразу.
 */
export function initialSectionState(mode: ItemFormMode, values: ItemFormValues): SectionOpenState {
    const alwaysOpen = mode === 'create';
    return SECTION_ORDER.reduce((acc, id) => {
        if (id === 'photos' || id === 'basics') {
            acc[id] = true;
        } else {
            acc[id] = !alwaysOpen && isSectionFilled(id, values);
        }
        return acc;
    }, {} as SectionOpenState);
}
